import { useState, useEffect, useRef } from "react";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  useFormulaVariables,
  validateFormula,
  FormulaVariable,
  FormulaCalculation,
  FormulaOperator,
  FormulaFunction,
} from "@/hooks/useFormulaVariables";
import {
  Calculator,
  Variable,
  Plus,
  Minus,
  X,
  Divide,
  Parentheses,
  FunctionSquare,
  AlertCircle,
  CheckCircle,
  Info,
  Loader2,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface FormulaBuilderProps {
  templateId: number;
  formula: string;
  onFormulaChange: (formula: string) => void;
}

export function FormulaBuilder({
  templateId,
  formula,
  onFormulaChange,
}: FormulaBuilderProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [activeTab, setActiveTab] = useState("variables");

  const {
    variables,
    calculations,
    operators,
    functions,
    isLoading,
    error,
    formatVariableForFormula,
  } = useFormulaVariables(templateId);

  // Validar fórmula en tiempo real
  const availableVariableNames = [
    ...variables.map((v) => v.name),
    ...calculations.map((c) => `calc_${c.id}`),
  ];
  const validation = validateFormula(formula, availableVariableNames);

  // Insertar texto en la posición del cursor
  const insertAtCursor = (text: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newFormula =
      formula.substring(0, start) + text + formula.substring(end);

    onFormulaChange(newFormula);

    // Mover cursor después del texto insertado
    setTimeout(() => {
      textarea.focus();
      textarea.selectionStart = textarea.selectionEnd = start + text.length;
    }, 0);
  };

  // Handler para click en variable
  const handleVariableClick = (variable: FormulaVariable) => {
    insertAtCursor(formatVariableForFormula(variable.name));
  };

  // Handler para click en cálculo
  const handleCalculationClick = (calc: FormulaCalculation) => {
    insertAtCursor(`{calc_${calc.id}}`);
  };

  // Handler para click en operador
  const handleOperatorClick = (operator: FormulaOperator) => {
    insertAtCursor(` ${operator.symbol} `);
  };

  // Handler para click en función
  const handleFunctionClick = (func: FormulaFunction) => {
    insertAtCursor(`${func.name}()`);
    // Mover cursor dentro de los paréntesis
    setTimeout(() => {
      const textarea = textareaRef.current;
      if (textarea) {
        textarea.selectionStart = textarea.selectionEnd =
          textarea.selectionStart - 1;
      }
    }, 10);
  };

  // Obtener icono para operador
  const getOperatorIcon = (symbol: string) => {
    switch (symbol) {
      case "+":
        return <Plus className="h-4 w-4" />;
      case "-":
        return <Minus className="h-4 w-4" />;
      case "*":
        return <X className="h-4 w-4" />;
      case "/":
        return <Divide className="h-4 w-4" />;
      case "(":
      case ")":
        return <Parentheses className="h-4 w-4" />;
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Cargando variables...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Información */}
      <Alert className="border-blue-200 bg-blue-50/50">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-900 text-sm">
          <strong>Constructor de Fórmulas:</strong> Crea expresiones matemáticas
          usando variables del template. Las variables se muestran como{" "}
          <code className="bg-blue-100 px-1 rounded">{"{nombre}"}</code>.
        </AlertDescription>
      </Alert>

      {/* Editor de Fórmula */}
      <div className="space-y-2">
        <Label htmlFor="formula-editor">Fórmula</Label>
        <Textarea
          ref={textareaRef}
          id="formula-editor"
          value={formula}
          onChange={(e) => onFormulaChange(e.target.value)}
          onSelect={(e) =>
            setCursorPosition((e.target as HTMLTextAreaElement).selectionStart)
          }
          placeholder="Ej: ({comunicacion} + {liderazgo}) / 2"
          rows={4}
          className={`font-mono text-sm ${
            formula && !validation.isValid
              ? "border-red-500 focus-visible:ring-red-500"
              : formula && validation.isValid
              ? "border-green-500 focus-visible:ring-green-500"
              : ""
          }`}
        />

        {/* Indicador de validación */}
        {formula && (
          <div
            className={`flex items-center gap-2 text-sm ${
              validation.isValid ? "text-green-600" : "text-red-600"
            }`}
          >
            {validation.isValid ? (
              <>
                <CheckCircle className="h-4 w-4" />
                <span>
                  Fórmula válida -{" "}
                  {validation.usedVariables.length} variable(s) usada(s)
                </span>
              </>
            ) : (
              <>
                <AlertCircle className="h-4 w-4" />
                <span>{validation.error}</span>
              </>
            )}
          </div>
        )}
      </div>

      {/* Operadores rápidos */}
      <div className="flex flex-wrap gap-1">
        <span className="text-sm text-muted-foreground mr-2">Operadores:</span>
        {operators.map((op) => (
          <TooltipProvider key={op.symbol}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => handleOperatorClick(op)}
                >
                  {getOperatorIcon(op.symbol) || op.symbol}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{op.name}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ))}
      </div>

      {/* Tabs para variables, cálculos y funciones */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-sm font-medium">
            Insertar en fórmula
          </CardTitle>
          <CardDescription className="text-xs">
            Haz clic para agregar a la fórmula
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full justify-start rounded-none border-b bg-transparent h-auto p-0">
              <TabsTrigger
                value="variables"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent"
              >
                <Variable className="h-4 w-4 mr-1" />
                Variables ({variables.length})
              </TabsTrigger>
              <TabsTrigger
                value="calculations"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent"
              >
                <Calculator className="h-4 w-4 mr-1" />
                Cálculos ({calculations.length})
              </TabsTrigger>
              <TabsTrigger
                value="functions"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent"
              >
                <FunctionSquare className="h-4 w-4 mr-1" />
                Funciones ({functions.length})
              </TabsTrigger>
            </TabsList>

            {/* Variables */}
            <TabsContent value="variables" className="mt-0">
              <ScrollArea className="h-48">
                <div className="p-3 space-y-1">
                  {variables.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No hay variables numéricas configuradas en este template.
                      Primero configura mapeos de variables.
                    </p>
                  ) : (
                    variables.map((variable) => (
                      <button
                        key={variable.name}
                        type="button"
                        className="w-full text-left p-2 rounded hover:bg-muted/50 transition-colors flex items-center justify-between group"
                        onClick={() => handleVariableClick(variable)}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <code className="text-sm font-mono bg-muted px-1.5 py-0.5 rounded">
                              {"{" + variable.name + "}"}
                            </code>
                            <Badge variant="outline" className="text-xs">
                              {variable.sourceType}
                            </Badge>
                          </div>
                          {variable.description && (
                            <p className="text-xs text-muted-foreground mt-0.5 truncate">
                              {variable.description}
                            </p>
                          )}
                        </div>
                        <Plus className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                      </button>
                    ))
                  )}
                </div>
              </ScrollArea>
            </TabsContent>

            {/* Cálculos */}
            <TabsContent value="calculations" className="mt-0">
              <ScrollArea className="h-48">
                <div className="p-3 space-y-1">
                  {calculations.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No hay cálculos numéricos configurados. Los cálculos de
                      tipo "ranking" no pueden usarse en fórmulas.
                    </p>
                  ) : (
                    calculations.map((calc) => (
                      <button
                        key={calc.id}
                        type="button"
                        className="w-full text-left p-2 rounded hover:bg-muted/50 transition-colors flex items-center justify-between group"
                        onClick={() => handleCalculationClick(calc)}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">
                              {calc.name}
                            </span>
                            <Badge variant="secondary" className="text-xs">
                              {calc.calculationType}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {calc.description}
                          </p>
                        </div>
                        <Plus className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                      </button>
                    ))
                  )}
                </div>
              </ScrollArea>
            </TabsContent>

            {/* Funciones */}
            <TabsContent value="functions" className="mt-0">
              <ScrollArea className="h-48">
                <div className="p-3 grid grid-cols-2 gap-1">
                  {functions.map((func) => (
                    <TooltipProvider key={func.name}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="text-left p-2 rounded hover:bg-muted/50 transition-colors"
                            onClick={() => handleFunctionClick(func)}
                          >
                            <code className="text-sm font-mono text-primary">
                              {func.name}()
                            </code>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {func.description}
                            </p>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-xs">
                          <div className="space-y-1">
                            <p className="font-medium">{func.syntax}</p>
                            <p className="text-xs">{func.description}</p>
                            <p className="text-xs text-muted-foreground">
                              Ejemplo: {func.example}
                            </p>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ))}
                </div>
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Ejemplos */}
      <div className="rounded-lg border p-3 space-y-2 bg-muted/30">
        <p className="text-sm font-medium">Ejemplos de fórmulas:</p>
        <div className="grid grid-cols-1 gap-1 text-xs font-mono">
          <code className="bg-muted px-2 py-1 rounded">
            ({"{comunicacion}"} + {"{liderazgo}"}) / 2
          </code>
          <code className="bg-muted px-2 py-1 rounded">
            round({"{promedio_general}"} * 10) / 10
          </code>
          <code className="bg-muted px-2 py-1 rounded">
            max({"{equipo_avg}"}, {"{referidos_avg}"})
          </code>
        </div>
      </div>
    </div>
  );
}
